<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { infoBoxIdEnum, infoBoxToneEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { expenseClaimDeclarationAsync, formatExpenseMoney, sumExpenseAmounts } from '@/composables/expense-claim'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import GridDetailInfo from '@/components/shared/GridDetailInfo.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ExpenseRowsSelector from '@/components/accounting/ExpenseRowsSelector.vue'

const MyExpensesGrid = defineAsyncComponent(() => import('@/components/accounting/MyExpensesGrid.vue'))

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  preselectedRowIds: {
    type: Array,
    default: () => [],
  },
  projectId: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()

const STEP = Object.freeze({ ROWS: 0, SUMMARY: 1 })

const isEdit = !!props.modelValue.id
const { step } = useStepper({ step: STEP.ROWS })

const staffId = props.modelValue.staff ?? webSiteStore.staffId
const rows = ref([])
const areRowsLoaded = ref(false)
const claimRowsSource = { id: props.modelValue.id, staff: staffId }
const selectedIds = ref([])
const initialIds = ref([])
const declaration = ref('')
const declarationAccepted = ref(false)
const isSaving = ref(false)
const showDelete = ref(false)

const onCancel = () => emit('cancel')
useNavigator(onCancel)

const goTo = (target) => { step.value = target }

const selectedRows = computed(() => rows.value.filter(row => selectedIds.value.includes(row.id)))
const total = computed(() => sumExpenseAmounts(selectedRows.value))
const period = computed(() => {
  const dates = selectedRows.value.map(row => row.date).filter(Boolean).sort()
  return dates.length ? `${formatShortDate(dates[0])} – ${formatShortDate(dates[dates.length - 1])}` : ''
})
const staffLabel = computed(() => props.modelValue.staff_label ?? planningStore.getServiceName(planningStore.getStaffById(staffId)))
const summaryItems = computed(() => [
  { label: i18n.t('common.staff'), value: staffLabel.value },
  { label: i18n.t('common.expenseClaimPeriodFromRows'), value: period.value },
  { label: i18n.t('common.expenseItems'), value: selectedRows.value.length },
  { label: i18n.t('common.expenseClaimTotal'), value: formatExpenseMoney(total.value) },
].filter(item => item.value))
const canSend = computed(() => selectedIds.value.length > 0 && declarationAccepted.value)
const hasChanges = computed(() => selectedIds.value.length !== initialIds.value.length || selectedIds.value.some(id => !initialIds.value.includes(id)))
const rowsCaption = computed(() => `${i18n.t('common.expenseClaimRowsCount', { count: selectedIds.value.length }, selectedIds.value.length)} · ${formatExpenseMoney(total.value)}`)
const title = isEdit
  ? `${i18n.t('common.expenseClaim')} ${props.modelValue.number}`
  : i18n.t('common.expenseClaimNew')

const loadRowsAsync = async() => {
  rows.value = await accountingStore.fetchClaimableExpenseRowsAsync(staffId, props.modelValue.id)
  const rowIds = new Set(rows.value.map(row => row.id))
  initialIds.value = isEdit ? rows.value.filter(row => row.expense_claim === props.modelValue.id).map(row => row.id) : []
  const preselected = props.preselectedRowIds.filter(id => rowIds.has(id))
  const defaultIds = props.projectId ? rows.value.filter(row => row.project === props.projectId).map(row => row.id) : []
  if (!areRowsLoaded.value) selectedIds.value = isEdit ? [...initialIds.value] : preselected.length ? preselected : defaultIds
  areRowsLoaded.value = true
}

const saveAsync = async(submit) => {
  isSaving.value = true
  const texts = submit ? { declaration_text: declaration.value } : {}
  try {
    const claim = await accountingStore.saveExpenseClaimAsync({ row_ids: selectedIds.value, staff: staffId, submit, ...texts }, props.modelValue.id)
    notifySuccess(submit ? i18n.t('info.expenseClaimSubmitted', { number: claim.number }) : i18n.t('info.expenseClaimDraftSaved'))
    emit('update', claim)
  } finally {
    isSaving.value = false
  }
}

const onSendFromBar = () => {
  if (canSend.value) saveAsync(true)
  else goTo(STEP.SUMMARY)
}

const onDeleteAsync = async() => {
  await accountingStore.deleteExpenseClaimAsync(props.modelValue.id)
  emit('update', null)
}

onMounted(async() => {
  const [declarationTemplate] = await Promise.all([
    expenseClaimDeclarationAsync(),
    accountingStore.fetchCostReportRowTagsAsync({}, true),
    planningStore.fetchStaffsAsync({}, true),
    loadRowsAsync(),
  ])
  declaration.value = declarationTemplate
})
</script>

<template>
  <q-card class="dialog-card-flex">
    <confirm-dialog
      v-model="showDelete"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDeleteAsync"
    />
    <navigate-back :text="title" @cancel="onCancel" />

    <q-card-section class="scroll col q-pa-none" :class="{ 'dialog-card-content': isEdit }">
      <q-card-section class="q-px-sm">
        <info-box
          v-if="props.modelValue.rejection_reason"
          class="q-mx-sm"
          :tone="infoBoxToneEnum.WARNING"
          :message="i18n.t('common.expenseClaimRejectedBecause', { reason: props.modelValue.rejection_reason })"
        />
        <q-stepper
          v-model="step"
          class="q-py-none"
          vertical
          animated
        >
          <q-step
            :name="STEP.ROWS"
            :title="i18n.t('common.expenseClaimSteps.rows')"
            :caption="step > STEP.ROWS || isEdit ? rowsCaption : null"
            icon="mdi-format-list-checks"
            :done="isEdit || step > STEP.ROWS"
            :header-nav="isEdit"
          >
            <expense-rows-selector v-if="isMobile" v-model="selectedIds" :rows="rows" />
            <my-expenses-grid
              v-else-if="areRowsLoaded"
              v-model:selected-row-ids="selectedIds"
              :claim="claimRowsSource"
              style="height: 46vh;"
              @row-saved="loadRowsAsync"
            />
            <div class="row justify-between text-caption text-grey-7 q-px-xs q-pt-sm">
              <span>{{ i18n.t('common.expenseClaimSelected', { count: selectedRows.length, total: rows.length }) }}</span>
              <span class="text-weight-medium text-dark">{{ formatExpenseMoney(total) }}</span>
            </div>
            <info-box :id="infoBoxIdEnum.EXPENSE_CLAIM_ROWS" class="q-mt-md" :message="i18n.t('info.expenseClaimRowsHint')" />
            <stepper-navigation
              :continue="() => goTo(STEP.SUMMARY)"
              :disable="!selectedIds.length"
            />
          </q-step>

          <q-step
            :name="STEP.SUMMARY"
            :title="i18n.t('common.expenseClaimSteps.summary')"
            :caption="isEdit && step !== STEP.SUMMARY ? period : null"
            icon="mdi-file-document-check-outline"
            :done="isEdit"
            :header-nav="isEdit"
          >
            <grid-detail-info :items="summaryItems" />
            <q-checkbox v-model="declarationAccepted" class="q-mt-sm items-start" :label="declaration" />
            <info-box :id="infoBoxIdEnum.EXPENSE_CLAIM_SUMMARY" class="q-mt-md" :message="i18n.t('info.expenseClaimSummaryHint')" />
            <stepper-navigation
              :back="() => goTo(STEP.ROWS)"
              :finish="isEdit ? null : () => saveAsync(true)"
              :finish-label="i18n.t('common.addExpenseClaim')"
              :disable="!canSend"
              :loading="isSaving"
            >
              <q-btn
                v-if="!isEdit"
                flat
                :label="i18n.t('common.expenseClaimSaveDraft')"
                :disable="!selectedIds.length"
                :loading="isSaving"
                @click="saveAsync(false)"
              />
            </stepper-navigation>
          </q-step>
        </q-stepper>
      </q-card-section>
    </q-card-section>

    <q-card-actions v-if="isEdit" class="align-center dialog-card-bottom-bar q-gutter-sm">
      <q-btn
        flat
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDelete = true"
      />
      <q-btn
        v-if="hasChanges"
        class="large-btn"
        color="primary"
        :label="i18n.t('common.expenseClaimSaveDraft')"
        :disable="!selectedIds.length"
        :loading="isSaving"
        @click="saveAsync(false)"
      />
      <q-btn
        class="large-btn"
        color="positive"
        :label="i18n.t('common.submitForApproval')"
        :disable="!selectedIds.length"
        :loading="isSaving"
        @click="onSendFromBar"
      />
    </q-card-actions>
  </q-card>
</template>
